/**
 * Category Tree Helpers
 *
 * Plan 003 replaced five flat catalogue categories with 674 across three
 * levels (40 / 470 / 164), so the tree is now big enough that walking it on
 * every render matters. Everything here is built once from the response and
 * read from a Map afterwards.
 */

import type { CatalogCategoryTreeNode } from "../services/catalog-api.service";

export interface CategoryTreeEntry {
	node: CatalogCategoryTreeNode;
	/** Root-first ancestors of `node`, not including `node` itself. */
	ancestors: CatalogCategoryTreeNode[];
}

export type CategoryTreeIndex = ReadonlyMap<string, CategoryTreeEntry>;

/**
 * Index every node in the tree by id, keeping the path that reaches it.
 *
 * Storing the ancestors up front is what lets a bare `categoryId` -- from a URL
 * or a saved record -- reopen the drill-down at the right branch without
 * re-walking 674 nodes each time.
 */
export function indexCategoryTree(
	roots: readonly CatalogCategoryTreeNode[],
): CategoryTreeIndex {
	const index = new Map<string, CategoryTreeEntry>();

	function walk(
		nodes: readonly CatalogCategoryTreeNode[],
		ancestors: CatalogCategoryTreeNode[],
	): void {
		for (const node of nodes) {
			index.set(node.id, { node, ancestors });
			if (node.children.length > 0) {
				walk(node.children, [...ancestors, node]);
			}
		}
	}

	walk(roots, []);
	return index;
}

/**
 * A leaf is the only kind of category a product can be filed on, so it is the
 * only kind worth offering as a filter.
 */
export function isLeafCategory(node: CatalogCategoryTreeNode): boolean {
	return node.children.length === 0;
}

/**
 * The root-first path down to `id`, ending with the node itself.
 *
 * Returns an empty array for an unknown id, which is the normal state while the
 * tree is still loading -- callers must not read that as "no category".
 */
export function getCategoryPath(
	index: CategoryTreeIndex,
	id: string,
): CatalogCategoryTreeNode[] {
	const entry = id ? index.get(id) : undefined;
	return entry ? [...entry.ancestors, entry.node] : [];
}
